import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Loader from "../../components/common/Loader";
import Footer from "../../components/common/Footer";
import Menu from "../../components/common/Menu";
import Navbar from "../../components/common/Navbar";
import StartButton from "../../components/home/StartButton";
import { usePageLoader } from "../../hooks/usePageLoader";
import {useOrder, useOrderByShortId} from "../../services/tempeFabricationCachedClient";
import type { Order } from "../../services/tempeFabricationCachedClient";

// Form validation schema
const orderStatusSchema = yup.object().shape({
  email: yup.string().email("Invalid email address").required("Email is required"),
  shortOrderId: yup.string()
    .required("Order ID is required")
    .min(3, "Order ID must be at least 3 characters"),
});

interface OrderStatusFormData {
  email: string;
  shortOrderId: string;
}

const OrderStatus: React.FC = () => {
  usePageLoader();
  const { orderId } = useParams<{ orderId?: string }>();
  const [searchParams, setSearchParams] = useState<{ shortOrderId: string; email: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OrderStatusFormData>({
    resolver: yupResolver(orderStatusSchema),
  });

  // Direct UUID lookup (when accessing via /order-status/:orderId link)
  const directLookup = useOrder(
    orderId || "",
    !!orderId // Only enabled when orderId param exists
  );

  // Form-based lookup (when user submits email + shortOrderId)
  const formLookup = useOrderByShortId(
    searchParams?.shortOrderId || "",
    searchParams?.email || "",
    !!searchParams && !orderId // Only enabled when form submitted and no direct orderId
  );

  const onSubmit = (formData: OrderStatusFormData) => {
    console.log("[OrderStatus] Checking order status with:", formData);
    setSearchParams({
      shortOrderId: formData.shortOrderId,
      email: formData.email,
    });
  };

  // Use the appropriate data source based on lookup method
  const { data, isLoading, error } = orderId ? directLookup : formLookup;
  const order = data?.result;
  const hasSearched = !!searchParams || !!orderId;

  // Helper function to render order status badge
  const renderStatusBadge = (status: string) => {
    const statusColors = {
      DELIVERED: { bg: "#d4edda", color: "#155724" },
      SHIPPED: { bg: "#cce5ff", color: "#004085" },
      PARTIALLY_SHIPPED: { bg: "#cce5ff", color: "#004085" },
      CANCELLED: { bg: "#f8d7da", color: "#721c24" },
      PACKING: { bg: "#fff3cd", color: "#856404" },
      PROCESSING: { bg: "#fff3cd", color: "#856404" },
      ORDER_PLACED: { bg: "#d1ecf1", color: "#0c5460" },
    };

    const colors = statusColors[status as keyof typeof statusColors] || { bg: "#e2e3e5", color: "#383d41" };

    return (
      <span style={{
        padding: "6px 16px",
        borderRadius: "16px",
        fontSize: "14px",
        fontWeight: "500",
        backgroundColor: colors.bg,
        color: colors.color,
      }}>
        {status.replace(/_/g, " ")}
      </span>
    );
  };

  // Helper function to render order details
  const renderOrderDetails = (order: Order) => (
    <div style={{ marginTop: "30px" }}>
      {/* Order Header */}
      <div style={{
        backgroundColor: "#fff",
        border: "1px solid #ddd",
        borderRadius: "8px",
        padding: "30px",
        marginBottom: "20px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "20px" }}>
          <div>
            <h3 style={{ margin: "0 0 10px 0", fontSize: "24px", fontWeight: "600" }}>
              Order ID {order.shortOrderId}
            </h3>
            <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
              Placed on {new Date(order.createdAt!!).toLocaleDateString()} at {new Date(order.createdAt!!).toLocaleTimeString()}
            </p>
          </div>
          {renderStatusBadge(order.status!!)}
        </div>

        {/* Customer Information */}
        <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
          <h5 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "15px" }}>Customer Information</h5>
          <div className="row">
            <div className="col-md-6">
              <p style={{ margin: "5px 0", fontSize: "14px", color: "#666" }}>
                <strong>Name:</strong> {order.customerName}
              </p>
            </div>
            <div className="col-md-6">
              <p style={{ margin: "5px 0", fontSize: "14px", color: "#666" }}>
                <strong>Email:</strong> {order.email}
              </p>
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
          <h5 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "10px" }}>Shipping Address</h5>
          <p style={{ margin: "3px 0", fontSize: "14px", color: "#666" }}>
            {order.shippingAddress!!.streetAddress}
          </p>
          {order.shippingAddress?.apartmentUnit && (
            <p style={{ margin: "3px 0", fontSize: "14px", color: "#666" }}>
              {order.shippingAddress.apartmentUnit}
            </p>
          )}
          <p style={{ margin: "3px 0", fontSize: "14px", color: "#666" }}>
            {order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.zipCode}
          </p>
        </div>

        {/* Shipment Tracking */}
        {order.orderShipments && order.orderShipments.length > 0 && (
          <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
            <h5 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "15px" }}>
              Tracking Information
            </h5>
            {order.orderShipments.map((shipment, index) => (
              <div key={index} style={{
                padding: "15px",
                backgroundColor: "#f9f9f9",
                borderRadius: "4px",
                marginBottom: "10px",
              }}>
                <p style={{ margin: "5px 0", fontSize: "14px" }}>
                  <strong>Carrier:</strong> {shipment.carrier}
                </p>
                <p style={{ margin: "5px 0", fontSize: "14px" }}>
                  <strong>Tracking ID:</strong> {shipment.trackingId}
                </p>
                {shipment.url && (
                  <a
                    href={shipment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "var(--color-orange1)",
                      textDecoration: "none",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Track Shipment →
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Order Status | Tempe Fabrication</title>
        <link rel="stylesheet" type="text/css" href="/home/assets/css/home_1_style.css" />
        <link rel="stylesheet" type="text/css" href="/innerpages/assets/css/innerpages.css" />
      </Helmet>
      <div className="inner-pages-style1">
        <Loader />
        <Menu />
        <div className="smooth-scroll-content" id="scrollsmoother-container">
          <Navbar currentPage="contact" />

          <header className="tc-inner-header-style1">
            <div className="container">
              <div className="info col-lg-8">
                <h2 className="js-title">Check Order Status</h2>
              </div>
            </div>
          </header>

          <section className="tc-product-detail-section" style={{ padding: "80px 0" }}>
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-6">
                  <div style={{
                    backgroundColor: "#f9f9f9",
                    padding: "40px",
                    borderRadius: "8px",
                  }}>
                    {/* Show form when NOT accessing via direct UUID link AND we haven't successfully loaded an order */}
                    {!orderId && !order && (
                      <>
                        <div style={{ textAlign: "center", marginBottom: "30px" }}>
                          <i className="fal fa-info-circle" style={{ fontSize: "48px", color: "var(--color-orange1)", marginBottom: "15px" }}></i>
                          <p style={{ color: "#666", lineHeight: "1.6" }}>
                            Enter your email address and order ID to check your order status.
                          </p>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="form-group mb-4">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Email Address *
                            </label>
                            <input
                              {...register("email")}
                              type="email"
                              className="form-control"
                              placeholder="your-email@example.com"
                              style={{
                                width: "100%",
                                padding: "12px 15px",
                                border: errors.email ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.email && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.email.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group mb-4">
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                              Order ID *
                            </label>
                            <input
                              {...register("shortOrderId")}
                              type="text"
                              className="form-control"
                              placeholder="ABC123"
                              style={{
                                width: "100%",
                                padding: "12px 15px",
                                border: errors.shortOrderId ? "1px solid #dc3545" : "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            />
                            {errors.shortOrderId && (
                              <span style={{ color: "#dc3545", fontSize: "14px" }}>
                                {errors.shortOrderId.message}
                              </span>
                            )}
                            <small style={{ color: "#999", fontSize: "12px", marginTop: "5px", display: "block" }}>
                              You can find your order ID in your confirmation email
                            </small>
                          </div>

                          <button
                            type="submit"
                            disabled={isLoading}
                            className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                            style={{
                              width: "100%",
                              padding: "15px",
                              fontSize: "16px",
                              fontWeight: "600",
                              opacity: isLoading ? 0.6 : 1,
                              cursor: isLoading ? "not-allowed" : "pointer",
                            }}
                          >
                            <span>{isLoading ? "Checking..." : "Check Order Status"}</span>
                          </button>
                        </form>

                        <div style={{
                          marginTop: "30px",
                          paddingTop: "20px",
                          borderTop: "1px solid #ddd",
                          textAlign: "center",
                        }}>
                          <p style={{ fontSize: "14px", color: "#999", marginBottom: "10px" }}>
                            Don't have your order ID?
                          </p>
                          <a
                            href="/find-order"
                            style={{
                              color: "var(--color-orange1)",
                              textDecoration: "none",
                              fontWeight: "500",
                            }}
                          >
                            Find My Order
                          </a>
                        </div>
                      </>
                    )}

                    {/* Show loading state */}
                    {isLoading && (
                      <div style={{ textAlign: "center", padding: "40px" }}>
                        <p style={{ color: "#666" }}>Loading order details...</p>
                      </div>
                    )}

                    {/* Show error message */}
                    {error && !isLoading && (
                      <div style={{
                        backgroundColor: "#f8d7da",
                        color: "#721c24",
                        padding: "20px",
                        borderRadius: "4px",
                        marginBottom: "20px",
                        textAlign: "center",
                      }}>
                        <i className="fal fa-exclamation-circle" style={{ fontSize: "32px", marginBottom: "10px", display: "block" }}></i>
                        <p style={{ margin: 0 }}>{error.message || "Failed to load order. Please try again."}</p>
                        {orderId && (
                          <a
                            href="/order-status"
                            style={{
                              color: "var(--color-orange1)",
                              textDecoration: "none",
                              fontWeight: "500",
                              marginTop: "15px",
                              display: "inline-block",
                            }}
                          >
                            Try Form-Based Lookup →
                          </a>
                        )}
                      </div>
                    )}

                    {/* Show order details after successful search or direct lookup */}
                    {!isLoading && order && renderOrderDetails(order)}

                    {/* Show "not found" message */}
                    {hasSearched && !isLoading && !order && !error && (
                      <div style={{
                        marginTop: "30px",
                        textAlign: "center",
                        padding: "20px",
                        backgroundColor: "#fff3cd",
                        color: "#856404",
                        borderRadius: "4px",
                      }}>
                        <i className="fal fa-inbox" style={{ fontSize: "32px", marginBottom: "10px", display: "block" }}></i>
                        <p style={{ margin: 0 }}>No order found with the provided information.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
        <StartButton />
      </div>
    </>
  );
};

export default OrderStatus;
